/**
 * SF-4653: one demo build's run (/demos/:id/progress). While Eva builds, every change shows waiting,
 * running, done or failed and the page reads the build again every few seconds. A failed change
 * shows its reason and "Carry on" (POST .../resume). When done: what was made and checked, the demo
 * login (shown once) and links to the company and to its delete option (the company page's danger
 * zone). A build with no run (drafting, in review, cancelled, failed before approval) goes to
 * /demos/:id instead.
 * SF-4746: someone else's build (All demos) is read only: no Carry on and no demo login. Every run
 * links to its plan and chat, read only.
 * SF-4848: a done build shows the quick links and the demo login first, then the log (what was
 * made, what was checked, the steps folded shut) and the delete link last. A build that is
 * running or failed looks as before: its steps at the top and the Demo company card at the bottom.
 * SF-4863: a done build with a failed check (fake data that failed or did not finish) shows what
 * did not work under its status.
 * SF-5050: a failed run sf-api lets the creator dismiss (it made nothing) shows Dismiss. A run whose
 * draft Eva no longer holds (DRAFT_EXPIRED) shows why and no Carry on, which could never work.
 */
import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useCan } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Collapse, Result, Space, Spin, Typography } from 'antd';
import { EnvironmentBanner, EnvironmentTag } from './components/EnvironmentBadge';
import { StatusTag } from './components/StatusTag';
import { RunSteps } from './components/RunSteps';
import { progressLine } from './run';
import { RunChecks, RunMade, RunProblems } from './components/RunResults';
import { DemoLoginPanel } from './components/DemoLoginPanel';
import { ReadOnlyNote } from './components/ReadOnlyNote';
import { QuickLinks } from './components/QuickLinks';
import { DismissDemo } from './components/DismissDemo';
import { WebAppLink } from '../../components/WebAppLink';
import {
  demoBuildQueryKey,
  DRAFT_EXPIRED,
  demoErrorMessage,
  progressRefetchInterval,
  resumeDemoBuildArgs,
  useDemoBuildProgress,
  useDemoMutation,
  type DemoBuildRunView,
} from './api/demoBuildsClient';
import { demoDetailPath, demoReviewPath, hasRun } from './constants';
import { useCanActOn } from './useCanActOn';

/** The new company's delete option. The danger zone sits on its General Info tab. */
const DeleteDemoLink: React.FC<{ companyId: string }> = ({ companyId }) => (
  <Space size={4} wrap>
    <Link to={`/companies/${encodeURIComponent(companyId)}`} data-testid="demo-delete-link">
      Delete this demo
    </Link>
    <Typography.Text type="secondary">
      (on the company page, General Info, Danger zone: Delete company)
    </Typography.Text>
  </Space>
);

/**
 * A running or failed build: the new company and its delete option.
 * SF-4770: and the web app in that company, when sf-api sends the link (it builds the address).
 */
const CompanyLinks: React.FC<{ companyId: string; webAppLink?: string | null }> = ({
  companyId,
  webAppLink,
}) => (
  <Space direction="vertical" size={4} data-testid="demo-company-links">
    <Link to={`/companies/${encodeURIComponent(companyId)}`}>Open the demo company</Link>
    {webAppLink && <WebAppLink href={webAppLink} />}
    <DeleteDemoLink companyId={companyId} />
  </Space>
);

const FailedRun: React.FC<{
  build: DemoBuildRunView;
  /** Someone else's build: the reason shows, Carry on and Dismiss do not. */
  readOnly: boolean;
  /** `answered` is the build sf-api returned for an accepted resume; none after a refusal. */
  onResumed: (answered?: DemoBuildRunView) => void;
}> = ({ build, readOnly, onResumed }) => {
  const { mutate, isLoading } = useDemoMutation<DemoBuildRunView>();
  const [problem, setProblem] = useState<string | null>(null);
  const failed = (build.steps ?? []).find((s) => s.status === 'failed');
  const expired = build.error_code === DRAFT_EXPIRED;
  // An expired draft is why it stopped, even when a change had failed before.
  const reason = expired ? build.error : failed?.error || build.error;

  const carryOn = () => {
    setProblem(null);
    mutate(resumeDemoBuildArgs(build.id), {
      onSuccess: (answer) => onResumed(answer?.data),
      onError: (error) => {
        setProblem(demoErrorMessage(error, 'Could not carry on. Try again.'));
        // The build may have moved on (another tab carried on, or it is not failed any more).
        onResumed();
      },
    });
  };

  return (
    <Alert
      type="error"
      showIcon
      data-testid="run-failed"
      message={failed ? `Not done: ${failed.summary || failed.ref}` : 'The build stopped'}
      description={
        <Space direction="vertical">
          {reason && <Typography.Text>{reason}</Typography.Text>}
          {!readOnly && !expired && (
            <>
              <Typography.Text type="secondary">
                Changes already made are kept. Carry on starts again from this change.
              </Typography.Text>
              {problem && <Alert type="warning" showIcon message={problem} />}
              <Button type="primary" loading={isLoading} onClick={carryOn}>
                Carry on
              </Button>
            </>
          )}
          {/* No longer dismissable (another tab): read the build again, as after a refused resume. */}
          {!readOnly && build.can_dismiss && (
            <DismissDemo buildId={build.id} onBuild={() => onResumed()} />
          )}
        </Space>
      }
    />
  );
};

const DemoProgressView: React.FC<{ id: string }> = ({ id }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useDemoBuildProgress(id, true);
  const loaded = query.data?.data;
  // Only the build in the URL (a previous one may still be in the cache).
  const build = loaded && loaded.id === id ? loaded : undefined;
  const polling = progressRefetchInterval(build) !== false;
  const canActOn = useCanActOn();

  // An accepted resume answers with the building record. It goes into the cache first, so the
  // page shows building and keeps reading even when the next read fails.
  const afterResume = (answered?: DemoBuildRunView) => {
    if (answered?.id === id && answered.status) {
      queryClient.setQueryData(demoBuildQueryKey(id), { data: answered });
    }
    void query.refetch();
  };

  if (build && !hasRun(build)) return <Navigate to={demoDetailPath(id)} replace />;
  const steps = build?.steps ?? [];
  const companyId = build?.created?.company_id;
  const readOnly = !canActOn(build);
  const done = build?.status === 'done';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <EnvironmentBanner />
      <Card
        title={build ? `Demo: ${build.inputs?.domain ?? ''}` : 'Demo'}
        extra={
          <Space>
            <Button onClick={() => navigate(demoReviewPath(id))}>View plan and chat</Button>
            <Button onClick={() => navigate('/demos')}>Back to demos</Button>
          </Space>
        }
      >
        {query.isLoading && <Spin />}
        {query.isError && !build && (
          <Alert
            type="error"
            showIcon
            message={demoErrorMessage(query.error, 'Could not load this demo.')}
          />
        )}
        {build && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {!build.is_mine && <ReadOnlyNote creator={build.created_by} />}
            {query.isError && (
              <Alert
                type="warning"
                showIcon
                message={
                  polling
                    ? 'Could not read the latest progress. Trying again in a few seconds.'
                    : 'Could not read the latest progress. Reload the page to try again.'
                }
              />
            )}
            <Space wrap>
              <StatusTag status={build.status} />
              <EnvironmentTag env={build.environment} />
              <Typography.Text data-testid="run-progress">
                {steps.length
                  ? progressLine(build.status, steps)
                  : build.status === 'building'
                    ? 'Eva is starting the build…'
                    : ''}
              </Typography.Text>
            </Space>
            {done && <RunProblems checks={build.checks ?? []} />}
            {build.status === 'failed' && (
              <FailedRun build={build} readOnly={readOnly} onResumed={afterResume} />
            )}
            {!done && steps.length > 0 && <RunSteps steps={steps} />}
          </Space>
        )}
      </Card>
      {build && done ? (
        <>
          {companyId && (
            <Card title="Quick links">
              <QuickLinks
                build={build}
                companyId={companyId}
                readOnly={readOnly}
                onRetry={() => void query.refetch()}
                retrying={query.isFetching}
              />
            </Card>
          )}
          {!readOnly && (
            <Card title="Demo login">
              <DemoLoginPanel
                buildId={build.id}
                login={build.demo_login}
                users={build.demo_users}
              />
            </Card>
          )}
          <Card title="What was made">
            <RunMade created={build.created} />
          </Card>
          <Card title="What was checked">
            <RunChecks checks={build.checks ?? []} />
          </Card>
          {steps.length > 0 && (
            <Collapse
              items={[
                {
                  key: 'steps',
                  label: `Steps (${steps.length})`,
                  children: <RunSteps steps={steps} />,
                },
              ]}
            />
          )}
          {companyId && (
            <Card size="small">
              <DeleteDemoLink companyId={companyId} />
            </Card>
          )}
        </>
      ) : (
        build &&
        companyId && (
          <Card title="Demo company">
            <CompanyLinks companyId={companyId} webAppLink={build.web_app_link} />
          </Card>
        )
      )}
    </Space>
  );
};

export const DemoProgressPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: can, isLoading: canLoading } = useCan({ resource: 'demos', action: 'show' });

  if (canLoading) return <Spin />;
  if (can?.can !== true) {
    return <Result status="403" title="403" subTitle="You do not have permission to see demos." />;
  }
  // Keyed by build so nothing (a shown password, a carry on answer) follows to another build.
  return id ? <DemoProgressView key={id} id={id} /> : null;
};
